import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { getPerfumeShops } from '../lib/api';
import { Gem, Plus, Eye, TrendingUp, ArrowRight, Sparkles } from 'lucide-react';

export default function PerfumeDashboard() {
    const [shops, setShops] = useState([]);
    const [loading, setLoading] = useState(true);
    const location = useLocation();

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getPerfumeShops();
            setShops(res.data);
        } catch { /* */ }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData, location.key]);

    const totalViews = shops.reduce((sum, s) => sum + (s.views || 0), 0);

    return (
        <DashboardLayout>
            <div className="space-y-8" data-testid="perfume-dashboard">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-semibold text-[#2B2A27] tracking-tight">Perfume Dashboard</h1>
                        <p className="text-[#69665B] mt-1">Manage your perfume shops and collections.</p>
                    </div>
                    <Link to="/dashboard/perfume/shops/new">
                        <Button className="bg-[#D4AF37] hover:bg-[#C9A227] text-white rounded-full gap-2" data-testid="create-perfume-shop-btn">
                            <Plus className="w-4 h-4" /> New Perfume Shop
                        </Button>
                    </Link>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <Card className="border-[#E5E1D1] shadow-sm bg-gradient-to-br from-[#D4AF37]/5 to-transparent">
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-[#69665B]">Total Shops</p>
                                    <p className="text-3xl font-semibold text-[#2B2A27] mt-1">{loading ? '...' : shops.length}</p>
                                </div>
                                <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center">
                                    <Gem className="w-6 h-6 text-[#D4AF37]" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="border-[#E5E1D1] shadow-sm">
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-[#69665B]">Total Views</p>
                                    <p className="text-3xl font-semibold text-[#2B2A27] mt-1">{loading ? '...' : totalViews}</p>
                                </div>
                                <div className="w-12 h-12 rounded-xl bg-[#4F7C61]/10 flex items-center justify-center">
                                    <Eye className="w-6 h-6 text-[#4F7C61]" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="border-[#E5E1D1] shadow-sm">
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-[#69665B]">Published</p>
                                    <p className="text-3xl font-semibold text-[#2B2A27] mt-1">{loading ? '...' : shops.filter(s => s.status === 'published').length}</p>
                                </div>
                                <div className="w-12 h-12 rounded-xl bg-[#D2924E]/10 flex items-center justify-center">
                                    <TrendingUp className="w-6 h-6 text-[#D2924E]" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <Card className="border-[#E5E1D1] shadow-sm">
                    <CardHeader className="flex flex-row items-center justify-between pb-3">
                        <CardTitle className="text-lg font-semibold text-[#2B2A27]">Perfume Shops</CardTitle>
                        <Link to="/dashboard/perfume/shops" className="text-[#D4AF37] hover:text-[#C9A227] text-sm font-medium flex items-center gap-1">
                            View all <ArrowRight className="w-4 h-4" />
                        </Link>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-14 bg-[#F0EDDF] rounded-xl animate-pulse" />)}</div>
                        ) : shops.length === 0 ? (
                            <div className="text-center py-12 text-[#69665B]">
                                <Sparkles className="w-10 h-10 mx-auto mb-3 text-[#969282]" />
                                <p className="text-sm mb-3">No perfume shops yet</p>
                                <Link to="/dashboard/perfume/shops/new">
                                    <Button className="bg-[#D4AF37] hover:bg-[#C9A227] text-white rounded-full text-sm">Create your first perfume shop</Button>
                                </Link>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {shops.slice(0, 5).map(shop => (
                                    <Link key={shop.id} to={`/dashboard/perfume/shops/${shop.id}`} className="flex items-center justify-between p-3 bg-[#F0EDDF]/50 rounded-xl hover:bg-[#F0EDDF] transition-colors" data-testid={`perfume-shop-item-${shop.id}`}>
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: shop.primary_color || '#1A1A2E' }}>
                                                <Gem className="w-5 h-5 text-white" />
                                            </div>
                                            <div>
                                                <p className="text-sm font-medium text-[#2B2A27]">{shop.name}</p>
                                                <p className="text-xs text-[#969282]">/catalog/{shop.slug}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${shop.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{shop.status}</span>
                                            <span className="text-xs text-[#69665B]">{shop.views || 0} views</span>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </DashboardLayout>
    );
}
